/**
 * Poki HTML5 SDK — loading, gameplay, commercialBreak, rewardedBreak.
 * Non-Poki builds set VITE_WITH_POKI=false so this module never touches window.PokiSDK.
 *
 * @see https://sdk.poki.com/html5
 * @see https://developers.poki.com/guide/sdk-overview
 */

import { devLog } from "./dev-log.js";

var POKI_SDK_SCRIPT_URL = "https://game-cdn.poki.com/scripts/v2/poki-sdk.js";
var PAGE_JUMP_KEYS = { ArrowDown: true, ArrowUp: true, " ": true };

var initPromise = null;
var enabled = false;
var loadingFinished = false;
var pageJumpGuardInstalled = false;
var gameplayActive = false;

function isPokiBuild() {
  return typeof import.meta !== "undefined" && import.meta.env && import.meta.env.VITE_WITH_POKI === "true";
}

function getSdk() {
  if (!isPokiBuild() || typeof window === "undefined") return null;
  return window.PokiSDK || null;
}

function refreshEnabled() {
  enabled = !!(isPokiBuild() && getSdk());
  return enabled;
}

function onPageJumpKeyDown(event) {
  if (!event || !PAGE_JUMP_KEYS[event.key]) return;
  event.preventDefault();
}

function onPageJumpWheel(event) {
  if (!event) return;
  event.preventDefault();
}

function withAdPause(game, startFn) {
  if (game && typeof game.setAdPaused === "function") {
    game.setAdPaused(true);
  }
  if (typeof startFn === "function") startFn();
}

function clearAdPause(game) {
  if (game && typeof game.setAdPaused === "function") {
    game.setAdPaused(false);
  }
}

export const PokiBridge = {
  ensureInitialized: function () {
    if (initPromise) return initPromise;
    if (!isPokiBuild()) {
      enabled = false;
      initPromise = Promise.resolve();
      return initPromise;
    }
    var sdk = getSdk();
    if (!sdk || typeof sdk.init !== "function") {
      enabled = false;
      initPromise = Promise.resolve();
      return initPromise;
    }
    devLog("[PokiBridge] PokiSDK.init()");
    initPromise = sdk
      .init()
      .then(function () {
        refreshEnabled();
      })
      .catch(function (err) {
        // Poki: still continue to the game when init rejects.
        console.warn("[PokiBridge] init failed, continuing:", err);
        refreshEnabled();
      });
    return initPromise;
  },

  isEnabled: function () {
    return !!enabled;
  },

  canOfferRewardedAds: function () {
    return !!enabled;
  },

  gameLoadingFinished: function () {
    if (loadingFinished) return;
    var sdk = getSdk();
    if (!sdk || typeof sdk.gameLoadingFinished !== "function") return;
    loadingFinished = true;
    try {
      devLog("[PokiBridge] PokiSDK.gameLoadingFinished()");
      sdk.gameLoadingFinished();
    } catch (e) {
      loadingFinished = false;
      console.warn("[PokiBridge] gameLoadingFinished failed:", e);
    }
  },

  gameplayStart: function () {
    if (!enabled || gameplayActive) return;
    var sdk = getSdk();
    if (!sdk || typeof sdk.gameplayStart !== "function") return;
    try {
      devLog("[PokiBridge] PokiSDK.gameplayStart()");
      sdk.gameplayStart();
      gameplayActive = true;
    } catch (e) {
      console.warn("[PokiBridge] gameplayStart failed:", e);
    }
  },

  gameplayStop: function () {
    if (!enabled || !gameplayActive) return;
    var sdk = getSdk();
    if (!sdk || typeof sdk.gameplayStop !== "function") return;
    try {
      devLog("[PokiBridge] PokiSDK.gameplayStop()");
      sdk.gameplayStop();
      gameplayActive = false;
    } catch (e) {
      console.warn("[PokiBridge] gameplayStop failed:", e);
    }
  },

  /**
   * Game Events checkpoint. `category/what/action` is the funnel step.
   * A day is `measure('stage', '1', 'start')`, the following night is `'2'`.
   * A tier is `measure('tier', '1', 'reached')`.
   * `/` and `^` are reserved by Poki for funnel keys.
   * @see https://developers.poki.com/guide/game-events
   * @param {string} category
   * @param {string} what
   * @param {string} action
   */
  measure: function (category, what, action) {
    if (!enabled) return;
    var sdk = getSdk();
    if (!sdk || typeof sdk.measure !== "function") return;
    var parts = [category, what, action].map(function (part) {
      return part == null ? "" : String(part);
    });
    for (var i = 0; i < parts.length; i++) {
      if (!parts[i] || parts[i].indexOf("/") !== -1 || parts[i].indexOf("^") !== -1) return;
    }
    try {
      devLog("[PokiBridge] PokiSDK.measure()", parts[0], parts[1], parts[2]);
      sdk.measure(parts[0], parts[1], parts[2]);
    } catch (e) {
      console.warn("[PokiBridge] measure failed:", e);
    }
  },

  /**
   * Natural-break interstitial. Poki decides whether an ad actually plays.
   * @param {{ game?: { setAdPaused?: (paused: boolean) => void } }} [options]
   * @returns {Promise<void>}
   */
  commercialBreak: function (options) {
    var opts = options || {};
    var game = opts.game;
    return PokiBridge.ensureInitialized().then(function () {
      var sdk = getSdk();
      if (!enabled || !sdk || typeof sdk.commercialBreak !== "function") {
        return;
      }
      return sdk
        .commercialBreak(function () {
          withAdPause(game);
        })
        .then(function () {
          clearAdPause(game);
        })
        .catch(function (err) {
          console.warn("[PokiBridge] commercialBreak failed:", err);
          clearAdPause(game);
        });
    });
  },

  /**
   * @param {{
   *   name?: string,
   *   onStarted?: () => void,
   *   onFinished?: () => void,
   *   onError?: (err: unknown) => void,
   *   game?: { setAdPaused?: (paused: boolean) => void }
   * }} [options]
   * @returns {Promise<{ granted: boolean, cancelled: boolean }>}
   */
  requestRewardedAd: function (options) {
    var opts = options || {};
    var started = typeof opts.onStarted === "function" ? opts.onStarted : null;
    var finished = typeof opts.onFinished === "function" ? opts.onFinished : null;
    var onError = typeof opts.onError === "function" ? opts.onError : null;
    var game = opts.game;

    return PokiBridge.ensureInitialized().then(function () {
      var sdk = getSdk();
      if (!enabled || !sdk || typeof sdk.rewardedBreak !== "function") {
        if (onError) onError(new Error("Poki rewarded ad unavailable"));
        return { granted: false, cancelled: false };
      }
      return sdk
        .rewardedBreak(function () {
          withAdPause(game, started);
        })
        .then(function (success) {
          clearAdPause(game);
          if (success) {
            if (finished) finished();
            return { granted: true, cancelled: false };
          }
          return { granted: false, cancelled: true };
        })
        .catch(function (err) {
          console.warn("[PokiBridge] rewardedBreak failed:", err);
          if (onError) onError(err);
          clearAdPause(game);
          return { granted: false, cancelled: false };
        });
    });
  },

  shouldShowPlayAgainInterstitial: function (game) {
    return !!(enabled && game);
  },

  /**
   * Block parent-page scroll when the game is iframed on poki.com.
   * @see https://sdk.poki.com/html5 — Prevent page jump
   */
  installPageJumpGuard: function () {
    if (pageJumpGuardInstalled || !isPokiBuild() || typeof window === "undefined") return;
    pageJumpGuardInstalled = true;
    window.addEventListener("keydown", onPageJumpKeyDown);
    window.addEventListener("wheel", onPageJumpWheel, { passive: false });
  },
};

export { POKI_SDK_SCRIPT_URL };

/** Test helper — reset singleton between tests. */
export function resetPokiBridgeForTests() {
  initPromise = null;
  enabled = false;
  loadingFinished = false;
  pageJumpGuardInstalled = false;
  gameplayActive = false;
}